// DensityAgenda — right pane beside the heatmap. Two sections:
//
//   1. The SELECTED day, always rendered — header + activities, or
//      header + "No activities on this date" if zero. The user
//      always sees the date they just clicked, with explicit feedback.
//
//   2. Next upcoming — scanning forward from selectedDate + 1, the
//      next TWO dates that carry ≥1 activity. Each date renders at
//      most 3 activities; the rest collapse to "+ N more" which the
//      caller wires to the existing DayPopover for that date.
//
// Activities within a date are sorted by severity then by time
// (same comparator as the calendar) so the most-urgent row leads.

import { useMemo } from "react";
import DensityAgendaItem from "./DensityAgendaItem";
import type { Activity, ActivitySeverity } from "../../../types/schedule";

interface DensityAgendaProps {
  selectedDate: string;
  activities: Activity[];
  today: Date;
  onOpen: (activity: Activity) => void;
  /** Optional handler for the "+ N more" affordance on a Next-upcoming
   *  date. The caller wires this to the existing DayPopover. When
   *  omitted, "+ N more" still renders but is non-interactive. */
  onShowAllForDate?: (iso: string) => void;
}

const MAX_PER_DATE = 3;
const MAX_NEXT_DATES = 2;

const SEVERITY_RANK: Record<ActivitySeverity, number> = {
  red: 0,
  brand: 1,
  amber: 2,
  ok: 3,
  sky: 4,
};

function isoOf(d: Date): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(
    d.getUTCDate(),
  ).padStart(2, "0")}`;
}

function formatDateHeading(iso: string, todayIso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  const weekday = d.toLocaleDateString("en-AU", {
    weekday: "long",
    timeZone: "UTC",
  });
  const dayNum = d.getUTCDate();
  const month = d.toLocaleDateString("en-AU", {
    month: "long",
    timeZone: "UTC",
  });
  const todayTag = iso === todayIso ? " · today" : "";
  return `${weekday} · ${dayNum} ${month}${todayTag}`;
}

function sortBySeverityThenTime(list: Activity[]): Activity[] {
  return [...list].sort((x, y) => {
    const s = SEVERITY_RANK[x.status] - SEVERITY_RANK[y.status];
    if (s !== 0) return s;
    const xt = x.time ?? "~";
    const yt = y.time ?? "~";
    return xt < yt ? -1 : xt > yt ? 1 : 0;
  });
}

export default function DensityAgenda({
  selectedDate,
  activities,
  today,
  onOpen,
  onShowAllForDate,
}: DensityAgendaProps) {
  const todayIso = isoOf(today);

  const byDate = useMemo(() => {
    const map = new Map<string, Activity[]>();
    for (const a of activities) {
      const list = map.get(a.date);
      if (list) list.push(a);
      else map.set(a.date, [a]);
    }
    for (const [iso, list] of map) {
      map.set(iso, sortBySeverityThenTime(list));
    }
    return map;
  }, [activities]);

  const selectedActivities = byDate.get(selectedDate) ?? [];

  const nextUpcoming = useMemo(() => {
    const futureDates = Array.from(byDate.keys())
      .filter((iso) => iso > selectedDate)
      .sort();
    return futureDates.slice(0, MAX_NEXT_DATES);
  }, [byDate, selectedDate]);

  return (
    <div className="space-y-6 overflow-y-auto">
      {/* Selected day */}
      <section data-testid="agenda-selected">
        <header data-testid="agenda-selected-header" className="mb-2">
          <h3 className="text-h4 font-semibold text-slate-900 dark:text-slate-50">
            {formatDateHeading(selectedDate, todayIso)}
          </h3>
        </header>
        {selectedActivities.length === 0 ? (
          <p
            data-testid="agenda-selected-empty"
            className="rounded-md border border-dashed border-slate-200 px-3 py-2 text-body-sm text-slate-500 dark:border-slate-700 dark:text-slate-400"
          >
            No activities on this date.
          </p>
        ) : (
          <div className="space-y-1">
            {selectedActivities.map((a) => (
              <DensityAgendaItem key={a.id} activity={a} onOpen={onOpen} />
            ))}
          </div>
        )}
      </section>

      {/* Next upcoming */}
      {nextUpcoming.length > 0 && (
        <section data-testid="agenda-next-upcoming">
          <h4 className="mb-2 text-overline font-medium text-slate-500 dark:text-slate-400">
            Next upcoming
          </h4>
          <div className="space-y-4">
            {nextUpcoming.map((iso) => {
              const list = byDate.get(iso) ?? [];
              const shown = list.slice(0, MAX_PER_DATE);
              const overflow = list.length - shown.length;
              return (
                <div key={iso} data-testid={`agenda-next-${iso}`}>
                  <div className="mb-1 text-body-sm font-medium text-slate-700 dark:text-slate-200">
                    {formatDateHeading(iso, todayIso)}
                  </div>
                  <div className="space-y-1">
                    {shown.map((a) => (
                      <DensityAgendaItem key={a.id} activity={a} onOpen={onOpen} />
                    ))}
                    {overflow > 0 && (
                      <button
                        type="button"
                        onClick={() => onShowAllForDate?.(iso)}
                        className="text-caption text-brand-600 hover:underline focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:text-brand-400"
                      >
                        + {overflow} more
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}
